import React from 'react';

import useClassy from '@core/hooks/useClassy';

import { sanitizeAnchorSegment } from '@ui/API/utils';
import Flex from '@ui/Flex';
import Icon from '@ui/Icon';

import classes from './style.module.scss';

interface Props {
  anchorId?: string;
  children?: React.ReactNode;
  className?: string;
  heading: React.ReactNode;
  /**
   * HTML tag to use for the heading element
   */
  headingTag?: 'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'span';
  /**
   * Renders the header in a title or subheader style
   */
  kind?: 'subheading' | 'title';
  shift?: string;
}

const APISectionHeader = React.memo(function APISectionHeader({
  anchorId,
  children,
  className,
  heading,
  headingTag = 'div',
  kind = 'subheading',
  ...props
}: Props) {
  const bem = useClassy(classes, 'APISectionHeader');

  if (!heading) {
    return null;
  }

  const safeAnchorId = anchorId ? sanitizeAnchorSegment(anchorId) || undefined : undefined;
  const headingText = typeof heading === 'string' ? heading.trim() : '';
  const anchorLabel = headingText ? `Skip link to ${headingText}` : 'Skip link to this section';

  const Tag = headingTag;

  return (
    <Flex
      align="center"
      className={bem('&', `_${kind}`, 'rm-APISectionHeader', className)}
      gap="4px"
      id={safeAnchorId}
      tag="header"
      {...props}
      wrap="wrap"
    >
      <Tag className={bem('-heading', kind === 'title' && '-heading_title')}>
        {safeAnchorId ? (
          <a aria-label={anchorLabel} className={bem('-anchor')} href={`#${safeAnchorId}`}>
            <Icon aria-hidden="true" name="anchor" size="sm" />
          </a>
        ) : null}
        {heading}
      </Tag>
      {children}
    </Flex>
  );
});

export default APISectionHeader;
